import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(isSupported);
    if (isSupported) {
      setPermission(Notification.permission);
    }
    // Browser API check runs once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported || !VAPID_KEY) return null;

    try {
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();

      if (!sub) {
        const result = await Notification.requestPermission();
        setPermission(result);
        if (result !== 'granted') return null;

        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_KEY)
        });
      }

      setSubscription(sub);

      await axios.post(`${API_URL}/api/push/subscribe`, sub.toJSON(), {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        withCredentials: true
      });

      return sub;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.warn('[Push] subscribe error:', err.message);
      return null;
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!subscription) return;
    try {
      await subscription.unsubscribe();
      setSubscription(null);
      await axios.post(`${API_URL}/api/push/unsubscribe`, { endpoint: subscription.endpoint }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.warn('[Push] unsubscribe error:', err.message);
    }
  }, [subscription]);

  return { permission, subscription, supported, subscribe, unsubscribe };
}
